import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import {
  ConsentimientoWidgetComponent,
  ConsentimientoWidgetSubmit,
} from '../../shared/components/consentimiento-widget/consentimiento-widget.component';
import {
  ConsentimientoSchema,
  CONSENTIMIENTO_SCHEMA_EJEMPLO,
  mapRawSchemaJson,
} from '../../core/models/consentimiento-schema.model';
import { ConsentimientoApiService, FormularioVigenteResponse } from '../../core/services/consentimiento/consentimiento-api.service';
import { environment } from '../../../environments/environment';

/**
 * Página que aloja el widget de consentimiento — hoy es la única forma de
 * verlo corriendo (no hay página host real todavía, ver Fase 2 pendiente).
 * Al cargar, intenta traer el formulario vigente real desde el backend; si
 * falla (backend apagado, CORS sin habilitar — ver ConsentimientoApiService),
 * cae al schema de ejemplo embebido para que la maqueta siga siendo visible.
 *
 * La carga del schema se reporta solo por consola (F12) — no hay pantalla de
 * error propia. El envío sí muestra feedback en el widget mismo: mensaje de
 * éxito + formulario vacío al enviar (a pedido explícito, 28-ago-2026), ver
 * envioExitosoEn/mostrarExito en ConsentimientoWidgetComponent.
 *
 * Fase 2 (empaquetado como Web Component): `codSociedad` pasa a ser un
 * @Input() en vez de una constante — Angular Elements mapea el atributo
 * HTML `cod-sociedad="..."` a este input automáticamente (kebab-case →
 * camelCase), sin código adicional. El default 'CONS-KAUF' se mantiene para
 * que la ruta /widget-consentimiento (que no pasa nada) siga funcionando
 * exactamente igual que antes.
 *
 * Fase 3 (28-ago-2026, ESP-FORM-003 §8, a pedido explícito): `codTipoFormulario`
 * es obligatorio del lado del backend — atributo HTML `cod-tipo-formulario="..."`.
 * Por eso acá tiene el mismo default 'CONSENT' que codSociedad tiene 'CONS-KAUF':
 * así una página que no lo especifique (como /widget-consentimiento hoy) lo
 * sigue mandando igual, sin romperse.
 *
 * Fase 4 (04-sep-2026, a pedido explícito de Roberto — "que se comporte como
 * modal"): `modal` es opcional y por defecto `false`, para no romper el
 * comportamiento actual (inline, ruta /widget-consentimiento). Con
 * `modal="true"` el widget se muestra como ventana emergente (fondo oscuro +
 * tarjeta centrada). `abierto` controla si se ve o no — el sitio host puede
 * volver a abrirlo seteando el atributo/propiedad desde JS (Angular Elements
 * mapea ambos). Se cierra con el botón ✕ (ya existía en
 * ConsentimientoWidgetComponent, pero no estaba conectado a nada) o
 * clickeando el fondo — en cualquiera de los dos casos emite `cerrado` para
 * que el sitio host pueda reaccionar (ej. sacar el listener del botón que lo
 * abrió).
 *
 * `tema` (08-sep-2026, a pedido explícito de Roberto — "fondo, color de base y
 * color de tipografía según la página correspondiente"): default 'kaufmann'
 * (antes era `null`/look neutro) — el resto de los defaults de esta misma
 * clase (`codSociedad='CONS-KAUF'`, `codTipoFormulario='CONSENT'`) ya asumen
 * Kaufmann, así que /widget-consentimiento sin parámetros ahora calza con
 * ellos. Además de colorear el propio widget (clase CSS sobre
 * ConsentimientoWidgetComponent, colores en sus variables --cw-*), acá mismo
 * también colorea el fondo de esta página de prueba/embebido y el título de
 * arriba (ver .wc-page en el HTML/SCSS) — mismos colores reales que
 * consentimiento-marca.component.scss, sin nav ni más copy.
 *
 * `cargando` (07-sep-2026, a pedido explícito): antes el widget se mostraba
 * de inmediato con el schema de ejemplo y, cuando llegaba la respuesta real
 * de GET /formularios/vigente, el título/subtítulo cambiaban en pantalla
 * (ej. de "Mantengamos el contacto" a "Consentimiento Cliente") — un cambio
 * de texto visible y confuso, más notorio todavía al abrirse como modal. Con
 * `cargando`, el widget no se muestra hasta que la llamada real resuelva
 * (éxito o error) — nunca se alcanza a ver el texto de ejemplo si hay
 * backend disponible. Si la llamada falla, igual se pasa a `false` para
 * mostrar el schema de ejemplo como respaldo (mismo comportamiento de
 * fallback de siempre, ver ngOnInit).
 */
@Component({
  selector: 'app-widget-consentimiento',
  standalone: true,
  imports: [ConsentimientoWidgetComponent],
  templateUrl: './widget-consentimiento.component.html',
  styleUrls: ['./widget-consentimiento.component.scss'],
})
export class WidgetConsentimientoComponent implements OnInit {
  @Input() codSociedad = 'CONS-KAUF';
  // 30-sep-2026, a pedido explícito: antes fijo en 'CONSENT' -- ese código solo existe en el
  // catálogo de QA/local, en prod el mismo tipo de formulario se llama 'ACEPTACION' (ver
  // environment.prod.ts). Tomarlo del environment hace que cada build traiga automáticamente
  // el código correcto, sin depender de que la página host lo pase explícito.
  @Input() codTipoFormulario = environment.codTipoFormulario;
  @Input() modal = false;
  /** 09-sep-2026: dónde se ancla el modal en la pantalla — 'centro' (el
   * original) o 'derecha' (la prueba puntual, estilo Bloomreach). Solo tiene
   * efecto con modal="true" — ver .demo-page--modal en el SCSS. */
  @Input() posicion: 'centro' | 'derecha' | 'izquierda' = 'centro';
  @Input() abierto = true;
  // Sin tema explícito, el Web Component es genérico: no agrega nombre ni
  // apariencia de una marca. Las páginas de Kaufmann y Andes Motor envían su
  // tema expresamente, por lo que conservan su presentación actual.
  @Input() tema: 'kaufmann' | 'andes-motor' | null = null;
  /**
   * 09-sep-2026, a pedido explícito de Roberto: el widget interno
   * (ConsentimientoWidgetComponent) ya no sabe de marcas, solo recibe estos 4
   * colores y se pinta con ellos — acá simplemente se reenvían. `tema` NO
   * desapareció: sigue siendo lo que decide el fondo/título de ESTA página
   * (.wc-page, ver HTML/SCSS), que es contenido propio de cada marca (texto,
   * no un color) y por eso queda fuera de este cambio. Los defaults acá son
   * los mismos colores que antes vivían "adentro" del widget para el tema
   * Kaufmann. El tema, en cambio, es neutro por defecto para que una página
   * externa pueda embeber solo el formulario sin mostrar una marca detrás.
   */
  @Input() colorTexto = '#ffffff';
  // #0A0C10EB = rgba(10, 12, 16, 0.92) en hex de 8 dígitos (#RRGGBBAA) — mismo
  // color/transparencia, solo en hexadecimal (09-sep-2026, a pedido explícito).
  @Input() colorFondo = '#0A0C10EB';
  @Input() colorBoton = '#00adef';
  @Input() colorTextoBoton = '#ffffff';
  /** 09-sep-2026, pedido puntual solo para /web1consentimiento — ver
   * docblock de ConsentimientoWidgetComponent.colorMasInfo. */
  @Input() colorMasInfo = '#1b2635';
  /**
   * 09-sep-2026, prueba puntual pedida por el usuario (comportarse como el
   * popup de Bloomreach hoy en vivo en maxus.cl): se cierra solo si nadie
   * interactúa. Por ahora solo se activa desde /consentimiento (ver
   * ConsentimientoGenericoComponent) — el resto de las páginas no la usan,
   * quedan con el mismo comportamiento de siempre.
   */
  @Input() ocultarSiNoHayInteraccion = false;
  @Input() segundosParaOcultar = 20;
  /** Por ahora oculto solo en la prueba de /consentimiento, a pedido
   * explícito ("por ahora no quitarla, ocultar") — no se borra el botón ni
   * su lógica, solo se deja de mostrar mientras se prueba este flujo. */
  @Input() mostrarBotonCerrar = true;
  /**
   * 15-sep-2026, a pedido explícito (Roberto): si la página host ya conoce al cliente (ej. lo
   * trae de su propio login/CRM), se los pasa acá — se mandan a GET /formularios/vigente
   * (ver ngOnInit) y, si vuelven en la respuesta (Cliente), precargan el formulario (ver
   * ConsentimientoWidgetComponent.datosCliente). Ninguno es obligatorio.
   */
  @Input() rut: string | null = null;
  @Input() nombres: string | null = null;
  @Input() apellidos: string | null = null;
  @Input() email: string | null = null;
  @Input() telefono: string | null = null;

  /**
   * A pedido explícito (25-sep-2026): identifica desde qué página se hizo el acto de
   * consentimiento -- lo define quien invoca el widget (ver ConsentimientoMarcaComponent), no
   * el formulario, porque el mismo formulario podría en teoría mostrarse en más de una página.
   */
  @Input() origen: string | null = 'consentimiento';
  /** A pedido explícito (25-sep-2026): 'web' por defecto (este widget hoy solo vive en la
   * web), pero la página que lo invoca puede pisarlo si alguna vez necesita otro valor -- mismo
   * criterio que `origen`, no queda 100% fijo sin excepción. */
  @Input() medio = 'web';
  @Output() cerrado = new EventEmitter<void>();
  private timeoutInactividad?: ReturnType<typeof setTimeout>;
  @ViewChild(ConsentimientoWidgetComponent) private widget?: ConsentimientoWidgetComponent;
  private readonly api = inject(ConsentimientoApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  schema: ConsentimientoSchema = CONSENTIMIENTO_SCHEMA_EJEMPLO;
  idFormularioVigente: string | null = null;
  /** true mientras espera la respuesta del POST — evita envíos duplicados si
   * el usuario clickea varias veces (cada click es un registro real). */
  enviando = false;
  /** true hasta que GET /formularios/vigente resuelva (éxito o error) — ver
   * docblock de la clase. Controla si se muestra o no el widget, no reemplaza
   * a `abierto` (ese lo controla el sitio host). */
  cargando = true;
  /** Lo que vino en Cliente de GET /formularios/vigente (null si no se mandó ningún dato
   * conocido, o si el backend no devolvió nada) — se reenvía tal cual al formulario interno. */
  datosCliente: FormularioVigenteResponse['Cliente'] = null;

  ngOnInit(): void {
    if (this.modal && this.ocultarSiNoHayInteraccion) {
      this.reiniciarTimeoutInactividad();
    }
    const datosConocidos = {
      rut: this.rut ?? undefined,
      nombres: this.nombres ?? undefined,
      apellidos: this.apellidos ?? undefined,
      email: this.email ?? undefined,
      telefono: this.telefono ?? undefined,
    };
    this.api.getFormularioVigente(this.codSociedad, this.codTipoFormulario, datosConocidos).subscribe({
      next: (res) => {
        try {
          const raw = JSON.parse(res.SchemaJson);
          this.schema = mapRawSchemaJson(raw);
          this.idFormularioVigente = res.id_formulario;
          this.datosCliente = res.Cliente ?? null;
          console.info('[widget-consentimiento] Schema real cargado desde GET /formularios/vigente');
        } catch (e: unknown) {
          const mensaje = e instanceof Error ? e.message : 'error desconocido';
          console.error('[widget-consentimiento] El SchemaJson real llegó pero no se pudo interpretar:', mensaje);
        }
        this.cargando = false;
      },
      error: (err: HttpErrorResponse) => {
        console.warn(
          '[widget-consentimiento] No se pudo cargar el formulario vigente real ' +
            '(¿backend apagado o CORS sin habilitar?) — mostrando el schema de ejemplo.',
          err,
        );
        this.cargando = false;
      },
    });
  }

  /** Botón ✕ del widget, o click en el fondo del modal (ver template). Sin
   * efecto si `modal` es false — no tiene sentido "cerrar" un widget inline.
   * `detectChanges()` explícito por el mismo motivo documentado en
   * ConsentimientoWidgetComponent.mostrarMensajeExitoYResetear(): verificado
   * en vivo (07-sep-2026) que sin esto el `@if (abierto)` del template no
   * siempre saca el modal de la pantalla pese a que `abierto` sí queda en
   * `false` — el mismo síntoma de Web Component standalone que ya se había
   * visto ahí. */
  onCerrar(): void {
    if (!this.modal) return;
    clearTimeout(this.timeoutInactividad);
    this.abierto = false;
    this.cerrado.emit();
    this.cdr.detectChanges();
  }

  /** Cualquier click/tecla dentro del panel (ver binding en el HTML) reinicia
   * la cuenta regresiva — si no vuelve a llamarse antes de que se cumpla,
   * cierra solo (mismo camino que la ✕, ver onCerrar). */
  reiniciarTimeoutInactividad(): void {
    if (!this.modal || !this.ocultarSiNoHayInteraccion) return;
    clearTimeout(this.timeoutInactividad);
    this.timeoutInactividad = setTimeout(() => this.onCerrar(), this.segundosParaOcultar * 1000);
  }

  onEnviar(payload: ConsentimientoWidgetSubmit): void {
    if (!this.idFormularioVigente || this.enviando) {
      if (!this.idFormularioVigente) {
        console.error(
          '[widget-consentimiento] No hay id_formulario real cargado (schema de ejemplo) — no se envía.',
        );
      }
      return;
    }

    const rutSinPuntos = payload.rut.replace(/\./g, '');
    this.enviando = true;

    this.api
      .postConsentimientos([
        {
          clave_idempotencia: `widget-frontend-${Date.now()}`,
          rut: rutSinPuntos,
          cliente: {
            nombres: payload.nombres,
            apellidos: payload.apellidos,
            email: payload.correo,
            telefono: payload.telefono,
          },
          aplica_al_grupo: false,
          id_formulario: this.idFormularioVigente,
          medio: this.medio,
          origen: this.origen ?? undefined,
          finalidades: payload.finalidades,
        },
      ])
      .subscribe({
        next: (res) => {
          this.enviando = false;
          this.widget?.mostrarMensajeExitoYResetear();
          console.info('[widget-consentimiento] Envío exitoso:', res);
        },
        error: (err: HttpErrorResponse) => {
          this.enviando = false;
          console.error('[widget-consentimiento] Falló el envío:', err);
        },
      });
  }
}
